<template>
    <editor-sidebar class="properties-seo" has-tabs floating :visible="visible">
        <template #header>
            <b-btn variant="light" class="mr-2" @click="$emit('close')" :title="'Cancel|Annuler'|tr">
                <i class="fas fa-close"/>
            </b-btn>
            <h4 class="sidebar-title">{{ tr(`SEO Assistant|Assistant SEO`) }}</h4>

            <b-dropdown v-if="aiReview" class="ml-auto" variant="primary">
                <template #button-content>
                    <i class="fas fa-magic-wand-sparkles mr-1"/> <span v-tr>AI Assistant|Assistant IA</span>
                </template>
                <b-dropdown-item @click="openAssistant(`analyze`)">
                    <i class="fas fa-search"/>
                    <span v-tr>Analyze Content|Analyser le contenu</span>
                </b-dropdown-item>
                <b-dropdown-item @click="openAssistant(`enhance`)">
                    <i class="fas fa-transporter-2"/>
                    <span v-tr>Optimize Text SEO|Optimiser le score SEO</span>
                </b-dropdown-item>
                <b-dropdown-item @click="openAssistant(`rewrite`)">
                    <i class="fas fa-typewriter"/>
                    <span v-tr>Enhance Text Writing|Améliorer la qualité du texte</span>
                </b-dropdown-item>
                <b-dropdown-item @click="openAssistant(`suggest`)">
                    <i class="fas fa-lightbulb-on"/>
                    <span v-tr>Suggest relevant/missing Content|Suggérer du contenu manquant/pertinent</span>
                </b-dropdown-item>
            </b-dropdown>
        </template>

        <b-tabs class="tabs-pills">
            <b-tab :title="tr(`Review|Analyse`)">
                <template v-if="aiReview">
                    <div class="text-center mt-3">
                        <chart-gauge class="mx-auto" :value="aiReview.overallScore"/>
                        <h6 v-tr>Overall SEO Score|Score SEO global</h6>
                    </div>

                    <sidebar-group title="Keywords|Mots-clés" no-left-padding>
                        <b-form-group :label="tr(`Global Keyword density|Densité globale des mots-clés`)">
                            <badge-range :value="aiReview.keywordDensity" :min-range="0.5" :max-range="1.5" :error-delta="0.3">
                                {{ aiReview.keywordDensity }}%
                            </badge-range>

                            <span class="hint"><span v-tr>Recommended|Recommandé</span> 0.5-1.5%</span>
                        </b-form-group>

                        <b-form-group :label="tr(`Page Keywords|Mots-clés de la page`)">
                            <badge-keyword v-for="(keyword, index) in pageChecker.SEO.keywords" :key="`seo-keywords-${index}`" :keyword="keyword"
                            class="mr-1 mb-1 "/>
                        </b-form-group>

                        <b-form-group :label="tr(`Semantic Coverage|Couverture sémantique`)">
                            {{ aiReview.semanticCoverageScore }}%
                        </b-form-group>

                        <b-form-group :label="tr(`Detected Topic|Sujet compris`)">
                            {{ aiReview.detectedTopic }}
                        </b-form-group>
                    </sidebar-group>

                    <sidebar-group title="Content Quality|Qualité du contenu" no-left-padding>
                        <b-form-group label="Text Overall Quality">
                            <b-progress>
                                <b-progress-bar :variant="scoreVariant(aiReview.textOverallScore)" :value="aiReview.textOverallScore">
                                    {{ aiReview.textOverallScore }}%
                                </b-progress-bar>
                            </b-progress>
                        </b-form-group>

                        <b-form-group :label="tr(`Text Quality|Qualité du texte`)">
                            {{ aiReview.textQuality }}
                        </b-form-group>

                        <b-form-group :label="tr(`Text Readability Evaluation (Flesch score)|Évaluation de la lisibilité (Flesch)`)">
                            <badge-range :value="aiReview.readabilityScore" :min-range="60" :max-range="70">
                                {{ aiReview.readabilityScore }}%
                            </badge-range>
                            <span class="hint" v-tr>Recommended 60-70|Recommandé 60-70</span>
                        </b-form-group>

                        <b-form-group :label="tr(`Words|Mots`)">
                            {{ pageChecker.stats.words|number }} <span v-tr>Words|Mots</span>
                            <span v-if="cmsPage.specs.words[displayLanguage]" class="hint"><span v-tr>Recommended|Recommandé</span> {{ cmsPage.specs.words[displayLanguage].min|number }}-{{ cmsPage.specs.words[displayLanguage].max|number }}</span>
                        </b-form-group>

                        <b-form-group :label="tr(`Titles|Titres`)">
                            {{ pageChecker.stats.titles|number }} <span v-tr>Titles|Titres</span>
                            <span v-if="cmsPage.specs.titles[displayLanguage]" class="hint"><span v-tr>Recommended|Recommandé</span> {{ cmsPage.specs.titles[displayLanguage].min|number }}-{{ cmsPage.specs.titles[displayLanguage].max|number }}</span>
                        </b-form-group>

<!--                        <b-form-group :label="tr(`Paragraphs|Paragraphes`)">-->
<!--                            {{ pageChecker.stats.paragraphs|number }} Words-->
<!--                            <span v-if="cmsPage.specs.paragraphs[displayLanguage]" class="hint">Recommended {{ cmsPage.specs.paragraphs[displayLanguage].min|number }}-{{ cmsPage.specs.paragraphs[displayLanguage].max|number }}</span>-->
<!--                        </b-form-group>-->

                        <b-form-group :label="tr(`Reading Time|Durée de lecture`)">
                            {{ pageChecker.stats.readTime }}min
                        </b-form-group>

                        <b-form-group :label="tr(`Avg Words / Paragraph|Moy. mots / paragraphes`)">
                            {{ aiReview.averageParagraphWords }} <span v-tr>Words|Mots</span>
                        </b-form-group>

                        <b-form-group>
                            <b-btn variant="light-primary" @click="openAssistant(`enhance`)" block>
                                <i class="fas fa-magic-wand-sparkles mr-1"/>
                                <span v-tr>Suggest better Text|Améliorer le texte</span>
                            </b-btn>
                        </b-form-group>

                        <b-form-group>
                            <b-btn variant="light-primary" @click="enhanceText" block>
                                <i class="fas fa-edit mr-1"/>
                                <span v-tr>Review Suggestions|Afficher les suggestions de texte</span>
                            </b-btn>
                        </b-form-group>
                    </sidebar-group>
                </template>

                <e-empty-placeholder v-else title="Not reviewed yet|Pas encore analysé">
                    <b-btn variant="primary" @click="openAssistant(`analyze`)">
                        <i class="fas fa-magic-wand-sparkles mr-1"/>
                        <span v-tr>Review with AI|Analyser par IA</span>
                    </b-btn>
                </e-empty-placeholder>
            </b-tab>
            <b-tab title="Page">
                <sidebar-group title="Page SEO Settings|Paramètres SEO de la page" no-left-padding>
                    <b-form-group :label="tr(`Preview Image|Image aperçu`)">
                        <b-img width="120" height="80" :src="cmsPage.meta.socialImage" blank-color="#ddd"/>
                    </b-form-group>

                    <b-form-group :label="tr(`SEO page Title|Titre de la page pour SEO`)">
                        <b-textarea v-model="cmsPage.meta.title[displayLanguage]" readonly/>
                    </b-form-group>

                    <b-form-group :label="tr(`SEO page Description|Description de la page pour SEO`)">
                        <b-textarea class="f-10" style="min-height: 150px" v-model="cmsPage.meta.description[displayLanguage]" readonly/>
                    </b-form-group>

                    <b-form-group>
                        <b-btn variant="light-primary" @click="$emit(`edit-page-seo`)" block>
                            <i class="fas fa-edit mr-1"/>
                            <span v-tr>Edit SEO Settings|Modifier les paramètres SEO</span>
                        </b-btn>
                    </b-form-group>
                </sidebar-group>
            </b-tab>
            <b-tab :title="tr(`Recommend.|Recommand.`)" v-if="aiReview">
                <sidebar-group title="List of Recommendations|Liste de recommandations" no-left-padding>
                    <div v-html="aiReview.seoRecommendations"/>
                </sidebar-group>
            </b-tab>
            <b-tab title="Suggestions" v-if="aiSuggestions">
                <sidebar-group title="Content|Contenu" no-left-padding>
                    <b-card v-for="(item, index) in aiSuggestions.content" :key="`t-${index}`" class="card-suggestion">
                        <details>
                            <summary>
                                <chart-gauge :value="item.score" size="xs"/>
                                {{item.title}}
                            </summary>
                            <div v-html="item.content"/>
                        </details>
                    </b-card>
                </sidebar-group>
                <sidebar-group title="Topics|Sujets" no-left-padding>
                    <b-card v-for="(item, index) in aiSuggestions.topics" :key="`t-${index}`" class="card-suggestion">
                        <chart-gauge :value="item.score" size="xs"/>
                        {{item.topic}}
                    </b-card>
                </sidebar-group>
                <sidebar-group title="Facts|Faits" no-left-padding>
                    <b-card v-for="(item, index) in aiSuggestions.facts" :key="`t-${index}`" class="card-suggestion">
                        <chart-gauge :value="item.score" size="xs"/>
                        {{item.fact || item.topic}}
                    </b-card>
                </sidebar-group>
                <sidebar-group title="FAQs" no-left-padding>
                    <b-card v-for="(item, index) in aiSuggestions.faq" :key="`t-${index}`" class="card-suggestion">
                        <details>
                            <summary>
                                <chart-gauge :value="item.score" size="xs"/>
                                {{item.question}}
                            </summary>
                            <div v-html="item.answer"/>
                        </details>
                    </b-card>
                </sidebar-group>
            </b-tab>
        </b-tabs>

        <b-modal v-model="assistant.modalVisible" :title="tr(`AI Assistant|Assistant IA`)">
            <b-form-group>
                <b-select v-model="assistant.type">
                    <option value="analyze" v-tr>Analyze Content|Analyser le contenu</option>
                    <option value="enhance" v-tr>Optimize Text SEO|Optimiser le score SEO</option>
                    <option value="rewrite" v-tr>Enhance Text Writing|Améliorer la qualité du texte</option>
                    <option value="suggest" v-tr>Suggest relevant/missing Content|Suggérer du contenu manquant/pertinent</option>
                </b-select>
            </b-form-group>

            <b-form-group label="AI Instructions">
                <b-textarea v-model="assistant.instructions"/>
                <div class="form-help mt-2" v-tr>
                    Add any important instruction for the AI to perform the task|
                    Ajouter n'importe quelle instruction pour l'IA afin d'effectuer la tâche plus efficacement
                </div>
            </b-form-group>

            <template #modal-footer>
                <b-btn @click="assistant.modalVisible = false">
                    <span v-tr>Cancel|Annuler</span>
                </b-btn>
                <b-btn variant="primary" @click="callAssistant">
                    <span v-tr>Call Assistant|Appliquer l'assistant</span>
                </b-btn>
            </template>
        </b-modal>
    </editor-sidebar>
</template>

<script>

// properties-seo #hot-reload-debug
import EditorSidebar from "@/layout/editor-sidebar.vue";
import SidebarGroup from "@components/editor/properties/components/sidebar-group.vue";
import EEmptyPlaceholder from "@vue-components/components/e-empty-placeholder.vue";
import EStatusBadge from "@vue-components/components/e-status-badge.vue";
import NetworkV2 from "@vue-components/helpers/NetworkV2.js";
import BadgeRange from "@components/editor/properties/seo/badge-range.vue";
import BadgeKeyword from "@components/editor/properties/seo/badge-keyword.vue";
import PageChecker from "@/helpers/review/PageChecker.js";
import {swal} from "vue-swal";
import ChartGauge from "@components/editor/properties/seo/chart-gauge.vue";

export default {
    name: `properties-seo`,
    components: {ChartGauge, BadgeKeyword, BadgeRange, EStatusBadge, EEmptyPlaceholder, SidebarGroup, EditorSidebar},
    props: {
        visible: {type: Boolean}
    },
    data() {
        return {
            assistant: {
                modalVisible: false,
                type: `analyze`,
                instructions: ``
            },
            interval: null
        }
    },
    computed: {
        aiReview() {
            return this.cmsPage.ai.review[this.displayLanguage];
        },
        aiEnhance() {
            return this.cmsPage.ai.enhance[this.displayLanguage];
        },
        aiRewrite() {
            return this.cmsPage.ai.rewrite[this.displayLanguage];
        },
        aiSuggestions() {
            return this.cmsPage.ai.suggestions[this.displayLanguage];
        },
        pageChecker() {
            return this.$store.state.assistant.pageChecker[this.displayLanguage];
        }
    },
    mounted() {
        this.interval = setInterval(() => {
            this.updateReview();
        },  3 * 1000);
    },
    beforeDestroy() {
        clearInterval(this.interval);
    },
    methods: {
        openAssistant(type) {
            if (!this.cmsPage.hiddenMeta.topic[this.displayLanguage]) {
                swal(this.$tr(`AI Assistant|Assistant IA`), this.$tr(`Please configure AI Assistant first.|Merci de configurer l'assistant IA pour commencer`), `info`)
                this.$emit(`edit-page-assistant-settings`);
                return;
            }

            this.assistant.modalVisible = true;
            this.assistant.type = type;
        },
        callAssistant() {
            switch (this.assistant.type) {
                case `analyze`:
                    this.reviewWithAI();
                    break;
                case `enhance`:
                    this.enhanceWithAI();
                    break;
                case `rewrite`:
                    this.rewriteWithAI();
                    break;
                case `suggest`:
                    this.suggestWithAI();
                    break;
            }
            this.assistant.modalVisible = false;
        },
        getAIPayload() {
            return {
                topic: this.cmsPage.hiddenMeta.topic[this.displayLanguage],
                mainKeywords: this.cmsPage.specs.mainKeywords[this.displayLanguage],
                keywords: this.cmsPage.specs.keywords[this.displayLanguage],
                audience: this.cmsPage.hiddenMeta.audience[this.displayLanguage],
                targetMarket: this.cmsPage.hiddenMeta.targetMarket[this.displayLanguage],
                competitors: this.cmsPage.hiddenMeta.competitors[this.displayLanguage],
                language: this.displayLanguage,
                content: this.pageChecker.textContent,
                instructions: this.assistant.instructions,
                words: this.pageChecker.stats.words[this.displayLanguage],
                minWords: this.cmsPage.specs.words[this.displayLanguage].min,
                maxWords: this.cmsPage.specs.words[this.displayLanguage].max
            }
        },
        reviewWithAI() {
            const data = {
                ...this.getAIPayload()
            };
            this.showLoading();
            NetworkV2.post(`/api/seo/assistant/page/review`, data, {
                timeout: 900000
            })
                .then(resp => {
                    this.$set(this.cmsPage.ai.review, this.displayLanguage, resp.data);
                    this.$store.state.assistant.ai.review[this.displayLanguage] = resp.data;
                    console.log(resp.data);
                    this.showSavedMessage(`Evaluation Completed`);
                })
                .catch(err => {
                    this.showErrorFeedback(err);
                    console.error(err);
                })
        },
        enhanceWithAI() {
            const data = {
                ...this.getAIPayload()
            };
            this.showLoading();
            NetworkV2.post(`/api/seo/assistant/page/enhance`, data, {
                timeout: 900000
            })
                .then(resp => {
                    this.$set(this.cmsPage.ai.enhance, this.displayLanguage, resp.data);
                    this.enhanceText();
                    this.hideLoading();
                })
                .catch(err => {
                    this.showErrorFeedback(err);
                    console.error(err);
                })
        },
        enhanceText() {
            if (!this.aiEnhance)
                return;

            this.$store.dispatch(`comparison/enableTextDiff`, {
                suggestions: this.aiEnhance.content,
                page: this.cmsPage,
                type: `suggestion`,
                language: this.displayLanguage
            });
        },
        rewriteWithAI() {
            const data = {
                ...this.getAIPayload()
            };
            this.showLoading();
            NetworkV2.post(`/api/seo/assistant/page/rewrite`, data, {
                timeout: 900000
            })
                .then(resp => {
                    this.$set(this.cmsPage.ai.rewrite, this.displayLanguage, resp.data);
                    this.rewriteText();
                    this.hideLoading();
                })
                .catch(err => {
                    this.showErrorFeedback(err);
                    console.error(err);
                })
        },
        rewriteText() {
            if (!this.aiRewrite)
                return;

            this.$store.dispatch(`comparison/enableTextDiff`, {
                suggestions: this.aiRewrite.content,
                page: this.cmsPage,
                type: `suggestion`,
                language: this.displayLanguage
            });
        },
        suggestWithAI() {
            const data = {
                ...this.getAIPayload()
            };
            this.showLoading();
            NetworkV2.post(`/api/seo/assistant/page/suggest/content`, data, {
                timeout: 900000
            })
                .then(resp => {
                    this.$set(this.cmsPage.ai.suggestions, this.displayLanguage, resp.data);
                    this.hideLoading();
                })
                .catch(err => {
                    this.showErrorFeedback(err);
                    console.error(err);
                })
        },
        scoreVariant(score) {
            if (score > 75)
                return `success`;
            else if (score > 50)
                return `warning`;
            else
                return `danger`;
        },
        updateReview() {
            this.$set(this.$store.state.assistant.pageChecker, this.displayLanguage, new PageChecker(this.cmsWebsite, this.cmsPage, this.displayLanguage));
            this.$forceUpdate();
        }
    }
}
</script>

<style lang="scss" scoped>
.hint {
    float: right;
    opacity: 0.75;
    margin-right: 5px;
}

.card-suggestion {
    user-select: text;
    margin-bottom: 5px;
    position: relative;

    .card-body {
        padding: 5px 10px;

        ::v-deep * {
            font-size: 10pt;
        }
    }

    .chart-gauge {
        float: right;
        display: block;
        margin-left: 3px;
        //position: absolute;
        //bottom: 2px;
        //right: 2px;
    }
}
</style>
